import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input, Textarea } from '@/components/ui/Input';
import { Lightbox } from '@/components/Lightbox';
import { AvatarChip } from '@/components/AvatarChip';
import { PostThumb, PostMediaBlock, usePostMedia } from '@/features/feed/PostMediaBlock';
import {
  CIRCLE_COVER_BGS,
  CIRCLE_COVER_EMOJIS,
  getCircleSeen,
  loadCircleCover,
  markCircleSeen,
  saveCircleCover,
} from '@/features/circle/circleSeen';
import { container } from '@/services/container';
import { useFeedStore } from '@/store/feed';
import { useChatStore } from '@/store/chat';
import { useSocialStore } from '@/store/social';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { CONTENT_MAX_IMAGES_PER_POST } from '@/config';
import type { Post } from '@shared/types';

/**
 * v3.20 朋友圈：好友动态流。
 *
 * 借鉴微信朋友圈的「好友可见时间线」体验，整合现有能力：
 * - 好友集合 = 聊天联系人（contacts）+ 链上关注（following）；
 * - 动态 = 好友/本人发布的广场帖子（帖子上链存证、行为挖矿不变，此处为好友聚焦视图）；
 * - 互动复用：点赞（链上 socialAct like）+ 评论（中继 commentPublish，与广场评论同库互通）；
 * - 支持发动态（文字/图片，复用 FeedService.publish）。
 *
 * v3.21 增强：封面/背景、可见范围（公开/好友/仅自己）、好友动态红点、图片九宫格放大墙。
 */

type PostScope = 'public' | 'friends' | 'private';

interface CircleComment {
  id: string;
  cid: string;
  author: string;
  text: string;
  ts: number;
}

function normalizeComment(raw: unknown): CircleComment | null {
  if (raw == null || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;
  const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
  const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
  const text = str(c.text).trim();
  if (!text) return null;
  return {
    id: str(c.id) || `c_${num(c.ts, Date.now())}`,
    cid: str(c.postCid) || str(c.cid),
    author: str(c.author) || (typeof c.authorPeer === 'string' ? c.authorPeer.slice(0, 10) : '匿名'),
    text,
    ts: num(c.ts, Date.now()),
  };
}

const COMMENTS_KEY = (cid: string): string => `mesh_comments:${cid}`;

/** v3.21 朋友圈：好友动态流页面。 */
export function FriendCirclePage(): JSX.Element {
  const navigate = useNavigate();
  const posts = useFeedStore((s) => s.posts);
  const stats = useFeedStore((s) => s.stats);
  const contacts = useChatStore((s) => s.contacts);
  const following = useSocialStore((s) => s.following);
  const selfDid = useIdentityStore((s) => s.did);

  // 好友集合：联系人 did + 关注 did + 自己
  const friendDids = useMemo(() => {
    const set = new Set<string>();
    for (const c of contacts) if (c.did) set.add(c.did.toLowerCase());
    for (const d of following) set.add(d.toLowerCase());
    if (selfDid) set.add(selfDid.toLowerCase());
    return set;
  }, [contacts, following, selfDid]);

  /** 可见范围过滤：private 仅本人；friends 好友/关注/本人；缺省 public。 */
  const friendPosts = useMemo(() => {
    const me = (selfDid ?? '').toLowerCase();
    return posts
      .filter((p) => {
        const author = (typeof p.authorDid === 'string' ? p.authorDid : '').toLowerCase();
        const sc = p.scope ?? 'public';
        if (sc === 'private') return me !== '' && author === me;
        if (sc === 'friends') return me !== '' && (author === me || friendDids.has(author));
        return friendDids.has(author);
      })
      .sort((a, b) => b.ts - a.ts);
  }, [posts, friendDids, selfDid]);

  // —— 红点（v3.21）：进入页面即视为已读 ——
  const [seenTs] = useState(() => getCircleSeen());
  useEffect(() => {
    markCircleSeen();
  }, []);
  const unseen = useMemo(() => friendPosts.filter((p) => p.ts > seenTs).length, [friendPosts, seenTs]);

  // —— 封面/背景（v3.21） ——
  const [cover, setCover] = useState<{ emoji: string; bg: string } | null>(() => loadCircleCover());
  const [coverOpen, setCoverOpen] = useState(false);

  // —— 发布框 ——
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [scope, setScope] = useState<PostScope>('public');
  const [preview, setPreview] = useState<{ cids: string[]; index: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const publish = async (): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed && files.length === 0) {
      useUiStore.getState().pushToast('error', '说点什么或选张图吧');
      return;
    }
    if (publishing) return;
    setPublishing(true);
    try {
      const post = await container.feed.publish({ text: trimmed, images: files, scope });
      useFeedStore.getState().bumpStat(post.id, 'views');
      setText('');
      setFiles([]);
      useUiStore.getState().pushToast('success', '已发布到朋友圈（帖子上链·发文即挖矿）');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setPublishing(false);
    }
  };

  const onPickFiles = (list: FileList | null): void => {
    if (!list) return;
    const next = [...files, ...Array.from(list)].slice(0, CONTENT_MAX_IMAGES_PER_POST);
    setFiles(next);
  };

  const friendCount = useMemo(() => {
    const set = new Set<string>();
    for (const c of contacts) if (c.did) set.add(c.did.toLowerCase());
    for (const d of following) set.add(d.toLowerCase());
    return set.size;
  }, [contacts, following]);

  return (
    <div className="space-y-4">
      {/* 封面（v3.21） */}
      <section
        className="card relative overflow-hidden p-0"
        style={{ background: cover?.bg ?? 'linear-gradient(135deg,#1e3a5f,#4a90d9)' }}
      >
        <div className="flex items-end justify-between p-5">
          <span className="text-5xl drop-shadow" aria-hidden>
            {cover?.emoji ?? '🌅'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="!border !border-white/30 !text-white"
            onClick={() => setCoverOpen((v) => !v)}
          >
            {coverOpen ? '收起' : '换封面'}
          </Button>
        </div>
        {coverOpen ? (
          <div className="border-t border-white/20 p-3">
            <p className="mb-2 text-[11px] text-white/80">背景</p>
            <div className="mb-3 flex flex-wrap gap-2">
              {CIRCLE_COVER_BGS.map((bg) => (
                <button
                  key={bg}
                  type="button"
                  aria-label="选择背景"
                  onClick={() => setCover({ emoji: cover?.emoji ?? '🌅', bg })}
                  className={`h-8 w-14 rounded-btn border-2 ${cover?.bg === bg ? 'border-white' : 'border-transparent'}`}
                  style={{ background: bg }}
                />
              ))}
            </div>
            <p className="mb-2 text-[11px] text-white/80">图案</p>
            <div className="flex flex-wrap gap-2">
              {CIRCLE_COVER_EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setCover({ emoji: e, bg: cover?.bg ?? CIRCLE_COVER_BGS[0] })}
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-lg ${
                    cover?.emoji === e ? 'bg-white/30' : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <Button size="sm" onClick={() => { saveCircleCover(cover ?? { emoji: '🌅', bg: CIRCLE_COVER_BGS[0] }); setCoverOpen(false); }}>
                完成
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="card p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">
            朋友圈 <span className="text-[11px] font-normal text-dim">好友动态 · {friendPosts.length}</span>
            {unseen > 0 ? (
              <span className="ml-1 inline-flex min-w-4 items-center justify-center rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-medium text-white">
                {unseen}
              </span>
            ) : null}
          </h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/discover')}>
            找好友{friendCount ? ` · ${friendCount}` : ''}
          </Button>
        </div>
        <p className="mt-1 text-[11px] text-dim">
          仅显示好友与关注对象的动态（帖子上链存证，行为挖矿不变；好友可见=👥，仅自己=🔒）
        </p>

        <div className="mt-3 space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="分享此刻…（朋友圈动态上链即挖矿）"
            rows={2}
          />
          <div className="flex flex-wrap items-center gap-2">
            {/* v3.21 可见范围 */}
            <div className="flex items-center gap-1 rounded-full bg-panel-2 p-1">
              {(
                [
                  { v: 'public', label: '🌍 公开' },
                  { v: 'friends', label: '👥 好友' },
                  { v: 'private', label: '🔒 仅自己' },
                ] as const
              ).map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setScope(o.v)}
                  className={`rounded-full px-2.5 py-1 text-[11px] transition-colors ${
                    scope === o.v ? 'bg-accent/20 text-accent' : 'text-dim hover:text-text'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => onPickFiles(e.target.files)}
            />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
              🖼 图片
            </Button>
            <span className="text-[11px] text-dim">
              {files.length > 0 ? `${files.length} 张已选` : '可配图'}
            </span>
            <Button size="sm" className="ml-auto" loading={publishing} onClick={() => void publish()}>
              发表
            </Button>
          </div>
        </div>
      </section>

      {friendPosts.length === 0 ? (
        <EmptyState
          title="朋友圈还没有动态"
          description="加好友或关注创作者后，他们的动态会出现在这里"
        />
      ) : (
        <ul className="space-y-3">
          {friendPosts.map((p) => (
            <CirclePostRow
              key={p.id}
              post={p}
              stat={stats[p.id] ?? {}}
              onPreview={(cids, index) => setPreview({ cids, index })}
            />
          ))}
        </ul>
      )}

      <PreviewLayer preview={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

/** v3.21 图片放大墙：支持多图翻页（九宫格点击进入）。 */
function PreviewLayer({
  preview,
  onClose,
}: {
  preview: { cids: string[]; index: number } | null;
  onClose: () => void;
}): JSX.Element | null {
  const [index, setIndex] = useState(0);
  const cids = preview?.cids ?? [];
  useEffect(() => {
    setIndex(preview?.index ?? 0);
  }, [preview]);
  const cid = cids[index] ?? '';
  const { url } = usePostMedia(cid);
  if (!preview || !cid || !url) return null;
  const prevBtn =
    cids.length > 1 ? (
      <button
        type="button"
        aria-label="上一张"
        onClick={(e) => {
          e.stopPropagation();
          setIndex((i) => (i - 1 + cids.length) % cids.length);
        }}
        className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 px-3 py-2 text-xl text-white hover:bg-black/70"
      >
        ‹
      </button>
    ) : null;
  const nextBtn =
    cids.length > 1 ? (
      <button
        type="button"
        aria-label="下一张"
        onClick={(e) => {
          e.stopPropagation();
          setIndex((i) => (i + 1) % cids.length);
        }}
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 px-3 py-2 text-xl text-white hover:bg-black/70"
      >
        ›
      </button>
    ) : null;
  return (
    <Lightbox url={url} onClose={onClose}>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-6">
        <div className="pointer-events-auto">{prevBtn}</div>
        <div className="pointer-events-auto">{nextBtn}</div>
      </div>
      <span className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-white">
        {index + 1} / {cids.length}
      </span>
    </Lightbox>
  );
}

/** 朋友圈动态行：头像/昵称/时间 + 正文 + 九宫格图片/媒体 + 赞/评论 + 统计 + 评论列表。 */
function CirclePostRow({
  post,
  stat,
  onPreview,
}: {
  post: Post;
  stat: Record<string, number>;
  onPreview: (cids: string[], index: number) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const liked = useFeedStore((s) => !!s.liked[post.id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [comments, setComments] = useState<CircleComment[]>(() => {
    try {
      const raw = window.localStorage.getItem(COMMENTS_KEY(post.manifestCid));
      const list = raw ? (JSON.parse(raw) as CircleComment[]) : [];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  });
  const [commentText, setCommentText] = useState('');
  const [commentOpen, setCommentOpen] = useState(false);
  const [sending, setSending] = useState(false);
  /** v3.22 打赏弹窗 */
  const [tipOpen, setTipOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState('1');
  const [tipNote, setTipNote] = useState('');

  const authorDid = typeof post.authorDid === 'string' ? post.authorDid : '';
  const images = Array.isArray(post.images) ? post.images : [];
  const cids = images.map((img) => img.cid).filter(Boolean);
  const cid = post.manifestCid;
  const scopeLabel =
    post.scope === 'private' ? '🔒 仅自己' : post.scope === 'friends' ? '👥 好友' : '🌍 公开';

  useEffect(() => {
    if (!commentOpen || !cid) return;
    let active = true;
    void container.feed
      .commentHistory(cid)
      .then((list) => {
        if (!active) return;
        const merged: CircleComment[] = [];
        const map = new Map<string, CircleComment>();
        for (const it of comments) map.set(it.id, it);
        for (const raw of list) {
          const it = normalizeComment(raw);
          if (it && !map.has(it.id)) map.set(it.id, it);
        }
        merged.push(...map.values());
        merged.sort((a, b) => a.ts - b.ts);
        setComments(merged);
        try {
          window.localStorage.setItem(COMMENTS_KEY(cid), JSON.stringify(merged.slice(-50)));
        } catch {
          /* 存储满忽略 */
        }
      })
      .catch(() => undefined);
    container.feed.commentSubscribe(cid, (raw) => {
      const it = normalizeComment(raw);
      if (!it) return;
      setComments((prev) => {
        if (prev.some((x) => x.id === it.id)) return prev;
        const next = [...prev, it].sort((a, b) => a.ts - b.ts);
        try {
          window.localStorage.setItem(COMMENTS_KEY(cid), JSON.stringify(next.slice(-50)));
        } catch {
          /* 存储满忽略 */
        }
        return next;
      });
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentOpen, cid]);

  const toggleLike = async (): Promise<void> => {
    if (busy === 'like') return;
    setBusy('like');
    try {
      const addr = container.identity.address;
      if (!addr) throw new MeshLinkError('E_LOCKED');
      const cur = useFeedStore.getState().isLiked(post.id);
      if (!cur && cid) {
        await container.chain.socialAct({
          action: 'like',
          from: addr as `0x${string}`,
          target: cid,
          signature: container.identity.signObject({ action: 'like', from: addr, target: cid, ts: Date.now() }),
        });
      }
      useFeedStore.getState().setLiked(post.id, !cur);
      useFeedStore.getState().bumpStat(post.id, 'likes', cur ? -1 : 1);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(null);
    }
  };

  /** v3.22 打赏：链上直转 DPB 给作者 + 本机统计。 */
  const onTip = async (): Promise<void> => {
    if (busy === 'tip') return;
    if (!authorDid) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    const toAddr = authorDid.startsWith('did:dpb:') ? authorDid.slice('did:dpb:'.length) : authorDid;
    if (toAddr === (container.identity.address ?? '').toLowerCase()) {
      useUiStore.getState().pushToast('error', '不能给自己打赏');
      return;
    }
    const n = Number(tipAmount);
    if (!Number.isFinite(n) || n <= 0) {
      useUiStore.getState().pushToast('error', '金额不正确');
      return;
    }
    setBusy('tip');
    try {
      await container.wallet.transfer(toAddr, tipAmount);
      useFeedStore.getState().bumpStat(post.id, 'tips', Math.round(n * 1000) / 1000);
      useUiStore.getState().pushToast('success', `已打赏 ${tipAmount} DPB（链上转账）`);
      setTipOpen(false);
      setTipNote('');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(null);
    }
  };

  const sendComment = async (): Promise<void> => {
    const trimmed = commentText.trim();
    if (!trimmed || !cid || sending) return;
    setSending(true);
    const item: CircleComment = {
      id: `c_${Date.now()}`,
      cid,
      author: container.identity.address?.slice(0, 10) || '匿名',
      text: trimmed,
      ts: Date.now(),
    };
    try {
      await container.feed.commentPublish(cid, item);
      useFeedStore.getState().bumpStat(post.id, 'comments');
      useUiStore.getState().pushToast('success', '评论已发送');
    } catch {
      useUiStore.getState().pushToast('error', '中继暂不可用，评论仅保存在本机');
    }
    setComments((prev) => {
      if (prev.some((x) => x.id === item.id)) return prev;
      const next = [...prev, item].sort((a, b) => a.ts - b.ts);
      try {
        window.localStorage.setItem(COMMENTS_KEY(cid), JSON.stringify(next.slice(-50)));
      } catch {
        /* 存储满忽略 */
      }
      return next;
    });
    setCommentText('');
    setSending(false);
  };

  return (
    <li className="card p-4">
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left"
        onClick={() => authorDid && navigate(`/u/${encodeURIComponent(authorDid)}`)}
      >
        <AvatarChip did={authorDid} name={post.authorName} size="md" />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="block truncate text-sm font-medium text-text">{post.authorName || '匿名用户'}</span>
            <span className="rounded-full bg-panel-2 px-1.5 py-0.5 text-[10px] text-dim">{scopeLabel}</span>
          </span>
          <span className="block text-[11px] text-dim">{new Date(post.ts).toLocaleString()}</span>
        </span>
      </button>

      {post.text ? <p className="mt-2 whitespace-pre-wrap text-sm text-text">{post.text}</p> : null}

      {/* v3.21 九宫格放大墙：多图 3 列网格 + 点击翻页放大 */}
      {images.length > 0 ? (
        <div className={`mt-3 grid gap-1.5 ${cids.length > 1 ? 'grid-cols-3' : 'grid-cols-1'}`}>
          {images.map((img, idx) => (
            <button
              key={img.cid}
              type="button"
              className={`overflow-hidden rounded-btn ${cids.length > 1 ? 'aspect-square' : 'max-h-80'}`}
              onClick={() => onPreview(cids, idx)}
            >
              <PostThumb cid={img.thumbCid || img.cid} fullCid={img.cid} onPreview={() => onPreview(cids, idx)} />
            </button>
          ))}
        </div>
      ) : null}

      {post.media ? <PostMediaBlock media={post.media} /> : null}

      <div className="mt-3 flex items-center gap-3 border-t border-line pt-2">
        <button
          type="button"
          disabled={busy === 'like'}
          className="text-xs text-dim hover:text-accent disabled:opacity-50"
          onClick={() => void toggleLike()}
        >
          {liked ? '❤️ 已赞' : '♡ 赞'}
        </button>
        <button
          type="button"
          className="text-xs text-dim hover:text-accent"
          onClick={() => setCommentOpen((v) => !v)}
        >
          💬 评论
        </button>
        <button
          type="button"
          disabled={busy === 'tip'}
          className="text-xs text-accent hover:text-accent-strong disabled:opacity-50"
          onClick={() => setTipOpen((v) => !v)}
        >
          🎁 打赏
        </button>
        {post.blockHeight ? <span className="mono ml-auto text-[11px] text-dim">#{post.blockHeight}</span> : null}
      </div>

      {stat.views || stat.likes || stat.reposts || stat.comments || stat.tips ? (
        <p className="mt-1 flex items-center gap-3 text-[11px] text-dim">
          {stat.views ? <span>👁 {stat.views}</span> : null}
          {stat.likes ? <span>❤️ {stat.likes}</span> : null}
          {stat.reposts ? <span>🔁 {stat.reposts}</span> : null}
          {stat.comments ? <span>💬 {stat.comments}</span> : null}
          {stat.tips ? <span>💰 {stat.tips} DPB</span> : null}
          <span className="ml-auto text-dim/70">本机聚合</span>
        </p>
      ) : null}

      {tipOpen ? (
        <div className="mt-3 space-y-2 rounded-btn bg-panel-2 p-3">
          <p className="text-xs font-medium text-text">🎁 打赏给 {post.authorName || '作者'}</p>
          <div className="flex flex-wrap gap-1.5">
            {['0.1', '0.5', '1', '5', '10'].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setTipAmount(v)}
                className={`rounded-full px-2.5 py-1 text-[11px] ${
                  tipAmount === v ? 'bg-accent/20 text-accent' : 'bg-panel-1 text-dim hover:text-text'
                }`}
              >
                {v} DPB
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-dim">金额</span>
            <Input
              value={tipAmount}
              onChange={(e) => setTipAmount(e.target.value)}
              inputMode="decimal"
              placeholder="自定义"
            />
            <span className="text-[11px] text-dim">备注</span>
            <Input
              value={tipNote}
              onChange={(e) => setTipNote(e.target.value)}
              placeholder="（可选）"
              className="max-w-28"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setTipOpen(false)}>
              取消
            </Button>
            <Button size="sm" loading={busy === 'tip'} onClick={() => void onTip()}>
              打赏
            </Button>
          </div>
        </div>
      ) : null}

      {commentOpen ? (
        <div className="mt-3 space-y-2 rounded-btn bg-panel-2 p-3">
          <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
            {comments.length === 0 ? (
              <li className="py-2 text-center text-dim">还没有评论</li>
            ) : (
              comments.map((c) => (
                <li key={c.id} className="text-dim">
                  <span className="font-medium text-text">{c.author}</span>：{c.text}
                </li>
              ))
            )}
          </ul>
          <div className="flex gap-2">
            <Input
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void sendComment();
              }}
              placeholder="友善评论，评论即挖矿…"
            />
            <Button size="sm" loading={sending} onClick={() => void sendComment()}>
              发送
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

export default FriendCirclePage;
